import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../StyledComponents/Dashboard";
import CustomeHeader from "../Admin/Common/CustomeHeader";
import TextError from "../../forms/TextError";
import "./OceanManualQuote.css";
import OceanManualQuoteHeader from "./OceanManualQuoteHeader";
import RateScopeSection from "./RateScopeSection";
import OriginInfoForm from "./OriginInfoForm";
import DestinationInfoForm from "./DestinationInfoForm";
import BasicCargoDetailsForm from "./BasicCargoDetailsForm";
import ChargesSection from "./ChargesSection";
import BottomSummaryBar from "./BottomSummaryBar";
import SectionCard from "./SectionCard";
import ProgressStepper from "./ProgressStepper";
import { CalendarClock, Clock } from "lucide-react";
import { shipment_list, shipport_list, updatenotificationdata } from "../../store/Admin/Common/CommonSlice";
import {
  addOceanManualQuote,
  clearOceanManualQuote,
  editOceanManualQuote,
  getAllOceanSwCharges,
  getOceanManualQuoteHistory,
} from "../../store/ManualQuote/OceanManualQuoteSlice";
import { grandTotalFor, rawChargeRate } from "./oceanQuoteMath";
import { emptyFreightOption } from "./SchedulesForm";
import { httpinstance } from "../../axios/api";
import { SPACE_AVAILABILITY_OPTIONS } from "../../utils/constant";

// get-all-sw-charges returns both air and sea master charges — 2 is the sea
// (ocean) air_sea_type, confirmed against a real response from
// manual-quote/get-all-sw-charges.
const SEA_AIR_TYPE = 2;

const CONTAINER_TYPE_CHOICES = ["20GP", "40GP", "40HC", "45HC", "20RF", "40RF", "20OT", "40OT", "20FR", "40FR"];

const zeroFreeDays = () => ({
  detention: 0,
  demurrage: 0,
  storage: 0,
  demurrageWithStorage: 0,
  combinedDnD: 0,
  combinedDnDWithStorage: 0,
});

const emptyChargeRow = (containerCount) => ({
  charge: "",
  chargeType: "origin",
  calcType: "SubjectTo",
  basis: "Per Container",
  currency: "INR",
  amounts: Array.from({ length: containerCount }, () => 0),
});

// Builds the editable container-type/qty rows the Buy/Sell charges table is
// keyed on. Real ocean queries carry a flat `containerDetails: [{ type,
// quantity, kgs }]` array (see Queries.jsx's ocean table + OceanCargoDetails.jsx) —
// fall back to a single generic column so the table still renders when this
// page is opened standalone, without a query loaded yet.
const buildContainersFromQuery = (queryDetails) => {
  const rows = Array.isArray(queryDetails?.containerDetails) ? queryDetails.containerDetails : [];
  if (rows.length === 0) return [{ type: "20GP", qty: 1 }];
  return rows.map((r) => ({ type: r?.type || "20GP", qty: Number(r?.quantity) || 1 }));
};

const OceanManualQuote = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queryDetails, manualQuoteDetails, viewOnly } = useSelector((state) => state.oceanManualQuoteSlice);
  const readOnly = !!viewOnly;

  const [loading, setLoading] = useState(false);
  const [rawPortList, setRawPortList] = useState([]);
  const [carrierList, setCarrierList] = useState([]);
  const [chargeList, setChargeList] = useState([]);

  // Quotation history (view-only mode) — mirrors the air form's history
  // panel: the list of past snapshots for this quote, and which one (if
  // other than the latest) is currently loaded into the read-only form below.
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewedVersion, setViewedVersion] = useState(null); // null = latest

  // "Reason for update" gate — Update rates (edit mode) doesn't submit
  // directly; it opens this modal first so the KAM states why an
  // already-submitted quote is being changed. Sent as `updateRemarks` in the
  // payload and recorded against this update's history version (backend
  // requires it non-empty on edit).
  const [showUpdateReasonModal, setShowUpdateReasonModal] = useState(false);
  const [updateReason, setUpdateReason] = useState("");
  const [updateReasonError, setUpdateReasonError] = useState("");
  // { USD: <INR per 1 USD>, ... } — Buy/Sell Charges rows can each be
  // entered in a different currency, so every total needs these rates to
  // normalize to INR before summing (see oceanQuoteMath's grandTotalFor).
  const [exchangeRates, setExchangeRates] = useState({});

  // list_ocean_port returns the full world port master (thousands of rows in
  // production vs. a handful in local/dev seed data) — fetched once and kept
  // as raw API rows so the {label, value} mapping below can be memoized
  // instead of re-running on every render.
  const fetchPortList = useCallback(() => {
    dispatch(shipport_list()).then((res) => {
      const ports = res?.payload?.data?.data || [];
      setRawPortList(ports);
    });
  }, [dispatch]);

  const portList = useMemo(
    () =>
      rawPortList.map((p) => ({
        label: p?.port_code ? `${p.port_name} - (${p.port_code})` : p?.port_name,
        value: p?.port_name,
      })),
    [rawPortList]
  );

  useEffect(() => {
    fetchPortList();
    dispatch(shipment_list()).then((res) => {
      const lines = res?.payload?.data?.data?.data || [];
      setCarrierList(
        lines.map((l) => ({
          label: l?.shipper_name || l?.shipping_line_code,
          value: l?.shipping_line_code,
        }))
      );
    });
    dispatch(getAllOceanSwCharges()).then((res) => {
      const charges = res?.payload || [];
      setChargeList(
        charges
          .filter((c) => c?.air_sea_type === SEA_AIR_TYPE)
          .map((c) => ({ label: c?.charge_name, value: c?._id }))
      );
    });
    httpinstance
      .get("admin/getexchangerates")
      .then((res) => {
        const rows = res?.data?.data || [];
        const rates = {};
        rows.forEach((r) => {
          if (r?.currency) rates[r.currency] = Number(r.inrRate) || 0;
        });
        setExchangeRates(rates);
      })
      .catch(() => {});
  }, [dispatch, fetchPortList]);

  // View-only mode: load this quote's version history so KAM can inspect and
  // open past snapshots (GET
  // ocean-manual-quote/get-manual-quote-history/:oceanManualQuoteId, newest-first).
  useEffect(() => {
    if (!readOnly || !manualQuoteDetails?._id) return;
    setHistoryLoading(true);
    dispatch(getOceanManualQuoteHistory(manualQuoteDetails._id))
      .then((res) => setHistoryEntries(res?.payload?.nodeData || []))
      .finally(() => setHistoryLoading(false));
  }, [dispatch, readOnly, manualQuoteDetails?._id]);

  const containers = buildContainersFromQuery(queryDetails);

  // queryDetails.originPort/destinationPort are already-formatted "City -
  // (CODE)" strings (see OceanManualQuoteHeader's portLabel, shown in the
  // route banner) — the port fields below store just the bare port name, so
  // strip the " - (CODE)" suffix to match what POL/POD etc. actually expect
  // as a value. Computed up front so both the initial autofill and the
  // origin/destination-match validation below use the exact same value.
  const extractPortName = (label) => (label || "").split(" - (")[0].trim();
  const queryOriginPortName = extractPortName(queryDetails?.originPort?.originPort);
  const queryDestPortName = extractPortName(queryDetails?.destinationPort?.destinationPort);

  // Extracted so the "Quotation history" panel (view-only mode) can rebuild
  // the same shape from a past snapshot doc via buildFormValues(entry.snapshot)
  // when the KAM opens/closes a past version.
  //
  // `doc` here is the raw saved OceanManualQuote document (or a history
  // snapshot of one) — its top-level fields (charges, freightCharges, totals,
  // queryId, ...) don't match this form's shape at all. `formSnapshot` is the
  // verbatim submitted form payload the backend now stores alongside those
  // computed fields specifically so this can be reconstructed; fall back to
  // spreading nothing for quotes saved before that field existed (they'll
  // just show the blank defaults below instead of erroring).
  // buildPayload deletes `schedules` from the submitted body (replacing it
  // with the computed `freightCharges`), so formSnapshot never has a
  // `schedules` key — only `freightCharges`. Rebuild `schedules` from that
  // instead, since the two share the same per-item shape (carrier,
  // departureDateAndTime, arrivalDateAndTime, transitDays, serviceType,
  // isDirect, routing) minus freightCharges' extra computed totals.
  const scheduleFromFreightCharges = (freightCharges) =>
    Array.isArray(freightCharges) && freightCharges.length > 0
      ? freightCharges.map((f) => ({
          carrier: f.carrier || "",
          departureDateAndTime: f.departureDateAndTime || null,
          arrivalDateAndTime: f.arrivalDateAndTime || null,
          transitDays: f.transitDays ?? "",
          serviceType: f.serviceType || "CY/CY",
          isDirect: f.isDirect !== false,
          routing: Array.isArray(f.routing) ? f.routing : [],
        }))
      : null;

  const buildFormValues = (doc) => {
    const snapshot = doc?.formSnapshot || {};
    const reconstructedSchedules = scheduleFromFreightCharges(snapshot.freightCharges);

    return {
    quoteExpiry: null,
    scope: {
      doorOrigin: false,
      inlandCyOrigin: false,
      cyOrigin: false,
      cyDestination: false,
      inlandCyDestination: false,
      doorDestination: false,
    },
    originInfo: {
      doorOrigin: "",
      pickupServiceType: "",
      pickupTransporter: "",
      pickupHaulageMode: "",
      pol: queryOriginPortName || "",
      inlandCyOrigin: queryOriginPortName || "",
      viaPortOrigin: "",
      originDepot: "",
    },
    destinationInfo: {
      pod: queryDestPortName || "",
      inlandCyDestination: queryDestPortName || "",
      viaPortDestination: "",
      destinationDepot: "",
      doorDestination: "",
      deliveryServiceType: "",
      deliveryTransporter: "",
      deliveryHaulageMode: "",
    },
    basicDetails: {
      carrier: "",
      serviceType: "CY / CY",
      spaceAvailability: SPACE_AVAILABILITY_OPTIONS[0].value,
      transitTime: "",
      sailing: "",
      startDate: null,
      rateExpiryDate: null,
      routingInfo: "",
    },
    cargoDetails: {
      cargoType: "FAK",
      commodity: "",
    },
    containers,
    buyCharges: [emptyChargeRow(containers.length)],
    sellCharges: [emptyChargeRow(containers.length)],
    schedules: [emptyFreightOption()],
    freeDays: [{ loadType: "FCL", origin: zeroFreeDays(), destination: zeroFreeDays() }],
    inclusions: "",
    exclusions: "",
    remarks: "",
    ...snapshot,
    ...(reconstructedSchedules ? { schedules: reconstructedSchedules } : {}),
    // `...snapshot` replaces basicDetails wholesale, so quotes saved before
    // Space Availability existed would reopen with it blank and fail
    // validation on edit — fall back to the default.
    ...(snapshot.basicDetails
      ? {
          basicDetails: {
            ...snapshot.basicDetails,
            spaceAvailability:
              snapshot.basicDetails.spaceAvailability ||
              SPACE_AVAILABILITY_OPTIONS[0].value,
          },
        }
      : {}),
    };
  };

  const initialValues = buildFormValues(manualQuoteDetails);

  const validationSchema = Yup.object({
    quoteExpiry: Yup.date().nullable().required("Expiry date is required"),
    basicDetails: Yup.object({
      carrier: Yup.string().required("Carrier is required"),
      spaceAvailability: Yup.number()
        .nullable()
        .required("Space availability is required"),
    }),
    buyCharges: Yup.array()
      .of(Yup.object({ charge: Yup.string().required("Charge is required") }))
      .min(1, "At least one buy charge is required"),
    sellCharges: Yup.array()
      .of(Yup.object({ charge: Yup.string().required("Charge is required") }))
      .min(1, "At least one sell charge is required"),
    // Same cross-field check ManualQuote (air) runs on freightCharges — the
    // Arrival DatePicker's disabledDate already blocks picking an invalid
    // date going forward, this catches the same thing at submit time (e.g.
    // an edit that starts from already-saved, now-inconsistent dates).
    schedules: Yup.array().of(
      Yup.object({
        arrivalDateAndTime: Yup.date()
          .nullable()
          .test("arrival-after-departure", "Arrival must be after departure", function (value) {
            const { departureDateAndTime } = this.parent;
            if (!value || !departureDateAndTime) return true;
            return dayjs(value).isAfter(dayjs(departureDateAndTime));
          }),
      })
    ),
  });

  // Turns whatever Formik/validateScopeFields put in `errors` into plain-
  // English labels so a failed Submit is never silent — some of these fields
  // (buyCharges/sellCharges) only exist in the DOM while their ChargesSection
  // tab is active, so without this summary the KAM would see nothing happen.
  const describeErrors = (errors) => {
    const labels = [];
    if (errors.quoteExpiry) labels.push("Quote expiry");
    if (errors.basicDetails?.carrier) labels.push("Carrier");
    if (errors.basicDetails?.spaceAvailability) labels.push("Space availability");
    if (errors.originInfo) labels.push(...Object.values(errors.originInfo));
    if (errors.destinationInfo) labels.push(...Object.values(errors.destinationInfo));
    if (errors.buyCharges) labels.push("Buy Charges — every row needs a charge selected");
    if (errors.sellCharges) labels.push("Sell Charges — every row needs a charge selected");
    if (Array.isArray(errors.schedules)) {
      errors.schedules.forEach((s, i) => {
        if (s?.arrivalDateAndTime) labels.push(`Freight option ${i + 1}: ${s.arrivalDateAndTime}`);
      });
    }
    return labels;
  };

  // Rate-scope-conditional requiredness lives outside the Yup schema since it
  // reads a sibling branch (`scope`) rather than a field on the same object —
  // same approach ManualQuote (air) uses for its cross-field checks.
  const validateScopeFields = (values) => {
    const errors = {};
    if (values.scope.doorOrigin && !values.originInfo.doorOrigin) {
      errors.originInfo = { ...errors.originInfo, doorOrigin: "Door Origin is required for the selected scope" };
    }
    if (values.scope.inlandCyOrigin && !values.originInfo.inlandCyOrigin) {
      errors.originInfo = { ...errors.originInfo, inlandCyOrigin: "Inland CY Origin is required for the selected scope" };
    } else if (
      values.scope.inlandCyOrigin &&
      queryOriginPortName &&
      values.originInfo.inlandCyOrigin !== queryOriginPortName
    ) {
      errors.originInfo = {
        ...errors.originInfo,
        inlandCyOrigin: `Inland CY Origin must match the query's origin port (${queryOriginPortName})`,
      };
    }
    if (values.scope.cyOrigin && !values.originInfo.pol) {
      errors.originInfo = { ...errors.originInfo, pol: "POL is required for the selected scope" };
    } else if (
      values.scope.cyOrigin &&
      queryOriginPortName &&
      values.originInfo.pol !== queryOriginPortName
    ) {
      // The quote must be for the same shipment the customer actually
      // queried — a KAM typing/picking a different POL than the query's own
      // origin (shown in the route banner) would quote a route the customer
      // never asked for.
      errors.originInfo = {
        ...errors.originInfo,
        pol: `POL must match the query's origin port (${queryOriginPortName})`,
      };
    }
    if (values.scope.doorDestination && !values.destinationInfo.doorDestination) {
      errors.destinationInfo = { ...errors.destinationInfo, doorDestination: "Door Destination is required for the selected scope" };
    }
    if (values.scope.inlandCyDestination && !values.destinationInfo.inlandCyDestination) {
      errors.destinationInfo = { ...errors.destinationInfo, inlandCyDestination: "Inland CY Destination is required for the selected scope" };
    } else if (
      values.scope.inlandCyDestination &&
      queryDestPortName &&
      values.destinationInfo.inlandCyDestination !== queryDestPortName
    ) {
      errors.destinationInfo = {
        ...errors.destinationInfo,
        inlandCyDestination: `Inland CY Destination must match the query's destination port (${queryDestPortName})`,
      };
    }
    if (values.scope.cyDestination && !values.destinationInfo.pod) {
      errors.destinationInfo = { ...errors.destinationInfo, pod: "POD is required for the selected scope" };
    } else if (
      values.scope.cyDestination &&
      queryDestPortName &&
      values.destinationInfo.pod !== queryDestPortName
    ) {
      errors.destinationInfo = {
        ...errors.destinationInfo,
        pod: `POD must match the query's destination port (${queryDestPortName})`,
      };
    }
    return errors;
  };

  // Drives both the section completion badges (SectionCard's green check)
  // and the progress stepper — deliberately looser than the Yup schema
  // (e.g. doesn't require every optional field) since this is a "looks
  // ready" signal, not a submit gate.
  const computeCompletion = (values) => {
    const scope = values.scope || {};
    const origin = values.originInfo || {};
    const destination = values.destinationInfo || {};
    const basic = values.basicDetails || {};
    return {
      "scope-section": Object.values(scope).some(Boolean),
      "origin-section":
        (scope.doorOrigin || scope.inlandCyOrigin || scope.cyOrigin) &&
        (!scope.doorOrigin || !!origin.doorOrigin) &&
        (!scope.inlandCyOrigin || !!origin.inlandCyOrigin) &&
        (!scope.cyOrigin || !!origin.pol),
      "destination-section":
        (scope.doorDestination || scope.inlandCyDestination || scope.cyDestination) &&
        (!scope.doorDestination || !!destination.doorDestination) &&
        (!scope.inlandCyDestination || !!destination.inlandCyDestination) &&
        (!scope.cyDestination || !!destination.pod),
      "basic-section": !!basic.carrier,
      "charges-section":
        (values.buyCharges || []).some((r) => r.charge) && (values.sellCharges || []).some((r) => r.charge),
    };
  };

  // Maps each freight option (values.schedules) to the submit payload's
  // `freightCharges[]` shape — carrier/validity/routing only; freight cost
  // itself is a regular line item in Buy/Sell Charges (see buildCharges),
  // not a rate carried on the option.
  const buildFreightCharges = (values) =>
    (values.schedules || []).map((option) => ({
      carrier: option.carrier,
      departureDateAndTime: option.departureDateAndTime,
      arrivalDateAndTime: option.arrivalDateAndTime,
      transitDays: Number(option.transitDays) || 0,
      serviceType: option.serviceType,
      isDirect: !!option.isDirect,
      routing: option.routing,
    }));

  // The backend's `charges` field is a flat array, one entry per (charge,
  // container type) — not our Buy/Sell tab split. Rows are matched across
  // the two tabs by their sw-charges `charge` id (the same id picked in
  // both tabs' Charge dropdown for the same line item). buyRate/sellRate
  // stay in the row's own currency here (not converted to INR) — only the
  // aggregate totals below need a common currency.
  const buildCharges = (values) => {
    const rowsByCharge = new Map();
    (values.buyCharges || []).forEach((row) => {
      if (!row.charge) return;
      rowsByCharge.set(row.charge, { ...(rowsByCharge.get(row.charge) || {}), buy: row });
    });
    (values.sellCharges || []).forEach((row) => {
      if (!row.charge) return;
      rowsByCharge.set(row.charge, { ...(rowsByCharge.get(row.charge) || {}), sell: row });
    });

    const charges = [];
    rowsByCharge.forEach(({ buy, sell }, chargeId) => {
      values.containers.forEach((c, ci) => {
        const quantity = Number(c.qty) || 0;
        const buyRate = buy ? rawChargeRate(buy, ci) : 0;
        const sellRate = sell ? rawChargeRate(sell, ci) : 0;
        charges.push({
          charge: chargeId,
          type: (buy || sell)?.chargeType,
          unit: (buy || sell)?.basis,
          currency: (buy || sell)?.currency,
          quantity,
          buyRate,
          sellRate,
          buyAmount: buyRate * quantity,
          sellAmount: sellRate * quantity,
        });
      });
    });
    return charges;
  };

  // Fills every field with placeholder data in one click, picking real
  // options from the already-loaded port/carrier/charge lists — and the
  // query's own origin/destination route, when there is one — so the
  // result is actually submittable — saves re-typing the same values by
  // hand on every test pass.
  const buildSampleValues = (currentValues) => {
    const originPort = queryOriginPortName || portList[0]?.value || "";
    const destPort = queryDestPortName || portList[0]?.value || "";
    const carrier = carrierList[0]?.value || "";
    const charge = chargeList[0]?.value || "";
    const now = dayjs();

    return {
      ...currentValues,
      quoteExpiry: now.add(7, "day"),
      scope: {
        doorOrigin: true,
        inlandCyOrigin: false,
        cyOrigin: true,
        cyDestination: true,
        inlandCyDestination: false,
        doorDestination: true,
      },
      originInfo: {
        doorOrigin: "Sample Door Origin Address",
        pickupServiceType: "CY/CY",
        // Pickup/Delivery Transporter is a live-searched field (see
        // AsyncTransporterSelect) with no static list to safely pick a
        // valid entry from here — leave it for the KAM to pick, rather than
        // stuffing in a value (e.g. the shipping-line carrier) that isn't
        // actually a transporter.
        pickupTransporter: "",
        pickupHaulageMode: "Road",
        pol: originPort,
        inlandCyOrigin: originPort,
        viaPortOrigin: "",
        originDepot: "Sample Origin Depot",
      },
      destinationInfo: {
        pod: destPort,
        inlandCyDestination: destPort,
        viaPortDestination: "",
        destinationDepot: "Sample Destination Depot",
        doorDestination: "Sample Door Destination Address",
        deliveryServiceType: "CY/CY",
        deliveryTransporter: "",
        deliveryHaulageMode: "Road",
      },
      basicDetails: {
        carrier,
        serviceType: "CY / CY",
        spaceAvailability: SPACE_AVAILABILITY_OPTIONS[0].value,
        transitTime: "20",
        sailing: "Direct",
        startDate: now,
        rateExpiryDate: now.add(7, "day"),
        routingInfo: "Sample routing info",
      },
      cargoDetails: {
        cargoType: "FAK",
        commodity: "Sample Commodity",
      },
      containers: [{ type: "20GP", qty: 1 }],
      buyCharges: [{ ...emptyChargeRow(1), charge, amounts: [100] }],
      sellCharges: [{ ...emptyChargeRow(1), charge, amounts: [150] }],
      schedules: [
        {
          ...emptyFreightOption(),
          carrier,
          departureDateAndTime: now,
          arrivalDateAndTime: now.add(20, "day"),
          transitDays: 20,
          routing: [
            {
              carrier,
              vesselName: "Sample Vessel",
              voyageNumber: "SV001",
              originPort,
              destinationPort: destPort,
              departure: now,
              arrival: now.add(20, "day"),
              transshipment: false,
            },
          ],
        },
      ],
      freeDays: [{ loadType: "FCL", origin: zeroFreeDays(), destination: zeroFreeDays() }],
      inclusions: "Sample inclusions",
      exclusions: "Sample exclusions",
      remarks: "Sample remarks",
    };
  };

  const buildPayload = (values) => {
    const payload = JSON.parse(JSON.stringify(values));
    payload.queryId = queryDetails?._id;
    // The OceanManualQuote schema/validator/cron-expiry-job all key off
    // `validityTo`, not `quoteExpiry` — that's the form's own internal field
    // name, kept for parity with the UI label. Without this mapping the
    // value was being silently dropped (schema has no `quoteExpiry` field),
    // so the quote was never actually saved with an expiry.
    payload.validityTo = payload.quoteExpiry;
    // One per quote (the ocean quote has a single carrier), so it sits at the
    // payload root rather than on each freightCharges[] option. 1 = Space
    // Available, 2 = Subject to Availability, straight from the Basic details
    // Space Availability dropdown.
    payload.space_availability = Number(values.basicDetails?.spaceAvailability);
    // KAM's stated reason for this change — only collected/required on edit
    // (see the "Reason for update" modal below); empty on create.
    payload.updateRemarks = updateReason;
    payload.containers = values.containers.map((c) => ({ type: c.type, qty: Number(c.qty) || 0 }));
    payload.buyCharges.forEach((r) => (r.amounts = r.amounts.map((a) => Number(a) || 0)));
    payload.sellCharges.forEach((r) => (r.amounts = r.amounts.map((a) => Number(a) || 0)));

    delete payload.schedules;
    payload.freightCharges = buildFreightCharges(values);
    payload.charges = buildCharges(values);

    const buyCharges = grandTotalFor(values.buyCharges, values.containers, exchangeRates);
    const sellCharges = grandTotalFor(values.sellCharges, values.containers, exchangeRates);
    payload.totals = {
      buyCharges,
      sellCharges,
      totalBuy: buyCharges,
      totalSell: sellCharges,
      profit: sellCharges - buyCharges,
    };
    payload.buyTotal = payload.totals.totalBuy;
    payload.sellTotal = payload.totals.totalSell;
    payload.earn = payload.totals.profit;

    // GST — same split OceanMainRateCom.jsx applies at booking time: the
    // Ocean Freight sell charge is taxed at 5%, every other sell charge
    // (all origin/destination accessorials — this form has no separate
    // "freight" chargeType, see buildFreightCharges' comment above) is taxed
    // at 18%. Identified by charge NAME rather than chargeType, since a
    // sell row's chargeType here is only ever "origin"/"destination".
    const GST_PERCENT_FREIGHT = 5;
    const GST_PERCENT_OTHER = 18;
    const isFreightChargeRow = (row) => {
      const label = chargeList.find((c) => c.value === row.charge)?.label || "";
      return ["ocean freight", "basic ocean freight"].includes(label.trim().toLowerCase());
    };
    const freightSellCharges = values.sellCharges.filter(isFreightChargeRow);
    const otherSellCharges = values.sellCharges.filter((r) => !isFreightChargeRow(r));
    const freightTaxable = grandTotalFor(freightSellCharges, values.containers, exchangeRates);
    const otherTaxable = grandTotalFor(otherSellCharges, values.containers, exchangeRates);
    const gstAmount = (freightTaxable * GST_PERCENT_FREIGHT) / 100 + (otherTaxable * GST_PERCENT_OTHER) / 100;

    payload.gstPercentageOnFreight = GST_PERCENT_FREIGHT;
    payload.gstPercentageOnOtherCharges = GST_PERCENT_OTHER;
    payload.taxableAmount = sellCharges;
    payload.gstAmount = gstAmount;
    payload.netAmount = sellCharges + gstAmount;

    return payload;
  };

  const onSubmit = (values, setErrors, resetForm) => {
    const scopeErrors = validateScopeFields(values);
    if (Object.keys(scopeErrors).length > 0) {
      setErrors(scopeErrors);
      return;
    }

    const payload = buildPayload(values);
    setLoading(true);

    const handleResult = (result) => {
      setLoading(false);
      const data = {
        message: result?.payload?.message,
        statusCode: result?.payload?.statusCode,
        success: result?.payload?.success,
      };
      if ([400, 404, 409].includes(result?.payload?.statusCode)) {
        dispatch(updatenotificationdata(data));
      } else if ([200, 201].includes(result?.payload?.statusCode)) {
        // Land back on the Ocean tab, not the Queries page's default Air tab.
        navigate("/QueriesKAM?cargoTab=2");
        dispatch(updatenotificationdata(data));
        dispatch(clearOceanManualQuote());
        resetForm();
        setUpdateReason("");
      }
    };

    if (Object.keys(manualQuoteDetails || {}).length > 0) {
      payload.manualqueryId = manualQuoteDetails?._id;
      dispatch(editOceanManualQuote(payload)).then(handleResult);
    } else {
      dispatch(addOceanManualQuote(payload)).then(handleResult);
    }
  };

  // Update rates (edit mode) doesn't submit straight away — it opens the
  // "Reason for update" modal first; Create still submits immediately since
  // there's nothing to justify yet. `formikInstance` is the render-prop's
  // `formik`, passed through explicitly since this fires from a plain button
  // click (type="button") outside Formik's own submit handling.
  const handleSubmitButtonClick = (formikInstance) => {
    if (Object.keys(manualQuoteDetails || {}).length > 0) {
      setUpdateReason("");
      setUpdateReasonError("");
      setShowUpdateReasonModal(true);
    } else {
      formikInstance.submitForm();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <MainContainer>
      <CustomeHeader
        type="cancel"
        title={readOnly ? "View Manual Quotation" : ""}
        description={readOnly ? "Read-only — this quotation cannot be edited here." : ""}
      />
      <OceanManualQuoteHeader queryDetails={queryDetails} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={false}
        onSubmit={(values, { setErrors, resetForm }) => onSubmit(values, setErrors, resetForm)}
      >
        {(formik) => {
          const completion = computeCompletion(formik.values);
          return (
          <Form autoComplete="off" className="mt-5 space-y-5">
            {!readOnly && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => formik.resetForm()}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg border border-gray-200 text-gray-600 bg-white transition-colors hover:bg-gray-50"
                  title="Clears every field back to how the form loaded"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => formik.setValues(buildSampleValues(formik.values))}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg border border-dashed transition-colors hover:bg-amber-50"
                  style={{ borderColor: "#F0C877", color: "#B86E00", background: "transparent" }}
                  title="Fills every field with placeholder data so you don't have to re-type the same test values every time"
                >
                  Autofill sample data
                </button>
              </div>
            )}

            <ProgressStepper completion={completion} />

            <SectionCard
              icon={CalendarClock}
              title="Quote details"
              subtitle="Set how long this quote stays valid"
              right={
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Expiry</span>
                  <div>
                    <Field id="quoteExpiry" name="quoteExpiry">
                      {() => (
                        <DateTimePicker
                          value={formik.values.quoteExpiry != null ? dayjs(formik.values.quoteExpiry) : null}
                          ampm={false}
                          format="DD/MM/YYYY HH:mm"
                          disablePast
                          disabled={readOnly}
                          onChange={(date) => formik.setFieldValue("quoteExpiry", date)}
                          slotProps={{
                            textField: {
                              size: "small",
                              placeholder: "Select date & time",
                              sx: { minWidth: 220 },
                            },
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="quoteExpiry" component={TextError} />
                  </div>
                </div>
              }
              bodyClassName="hidden"
            />

            <RateScopeSection formikData={formik} complete={completion["scope-section"]} readOnly={readOnly} />

            <OriginInfoForm formikData={formik} portList={portList} complete={completion["origin-section"]} readOnly={readOnly} />
            <DestinationInfoForm formikData={formik} portList={portList} complete={completion["destination-section"]} readOnly={readOnly} />
            <BasicCargoDetailsForm formikData={formik} carrierList={carrierList} complete={completion["basic-section"]} readOnly={readOnly} />

            <ChargesSection
              formikData={formik}
              chargeList={chargeList}
              portList={portList}
              carrierList={carrierList}
              exchangeRates={exchangeRates}
              complete={completion["charges-section"]}
              readOnly={readOnly}
              onAddContainer={() => {
                const usedTypes = new Set(formik.values.containers.map((c) => c.type));
                const nextType = CONTAINER_TYPE_CHOICES.find((t) => !usedTypes.has(t)) || "20GP";
                formik.setFieldValue("containers", [...formik.values.containers, { type: nextType, qty: 1 }]);
                formik.setFieldValue(
                  "buyCharges",
                  formik.values.buyCharges.map((r) => ({ ...r, amounts: [...r.amounts, 0] }))
                );
                formik.setFieldValue(
                  "sellCharges",
                  formik.values.sellCharges.map((r) => ({ ...r, amounts: [...r.amounts, 0] }))
                );
              }}
              onRemoveContainer={(index) => {
                formik.setFieldValue(
                  "containers",
                  formik.values.containers.filter((_, i) => i !== index)
                );
                formik.setFieldValue(
                  "buyCharges",
                  formik.values.buyCharges.map((r) => ({
                    ...r,
                    amounts: r.amounts.filter((_, i) => i !== index),
                  }))
                );
                formik.setFieldValue(
                  "sellCharges",
                  formik.values.sellCharges.map((r) => ({
                    ...r,
                    amounts: r.amounts.filter((_, i) => i !== index),
                  }))
                );
              }}
            />

            {formik.submitCount > 0 && describeErrors(formik.errors).length > 0 && (
              <div
                className="rounded-xl px-4 py-3 text-sm font-semibold"
                style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#B91C1C" }}
              >
                Complete these before submitting: {describeErrors(formik.errors).join(", ")}
              </div>
            )}

            <BottomSummaryBar
              formikData={formik}
              loading={loading}
              exchangeRates={exchangeRates}
              isEdit={Object.keys(manualQuoteDetails || {}).length > 0}
              readOnly={readOnly}
              onSubmitClick={() => handleSubmitButtonClick(formik)}
              onBack={() => {
                navigate("/QueriesKAM?cargoTab=2");
                dispatch(clearOceanManualQuote());
              }}
            />

            {/* "Reason for update" gate — Update rates (edit mode) opens this
                instead of submitting directly; Confirm runs Formik's own
                validation/submit via formik.submitForm(), same as a native
                submit click would. */}
            {showUpdateReasonModal && (
              <div
                className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
                onClick={() => setShowUpdateReasonModal(false)}
              >
                <div
                  className="bg-white rounded-2xl max-w-md w-full p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-base font-bold text-gray-800">Reason for update</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Let the team know why this quote is being updated — it's saved with the quote's history.
                  </p>
                  <textarea
                    value={updateReason}
                    onChange={(e) => {
                      setUpdateReason(e.target.value);
                      if (updateReasonError) setUpdateReasonError("");
                    }}
                    autoFocus
                    rows={4}
                    placeholder="e.g. Carrier revised the freight rate, customer requested a different container mix..."
                    className="mt-3 w-full p-3 text-sm text-gray-800 bg-white border border-gray-200 rounded-xl outline-none resize-y focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                  {updateReasonError && (
                    <p className="text-xs font-semibold mt-1.5" style={{ color: "#B91C1C" }}>
                      {updateReasonError}
                    </p>
                  )}
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowUpdateReasonModal(false)}
                      className="text-sm font-semibold px-4 py-2 rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!updateReason.trim()) {
                          setUpdateReasonError("Please enter a reason for this update.");
                          return;
                        }
                        setShowUpdateReasonModal(false);
                        formik.submitForm();
                      }}
                      className="text-sm font-bold px-4 py-2 rounded-lg text-gray-900"
                      style={{ background: "#FCB614" }}
                    >
                      Confirm &amp; Update
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quotation history — view-only mode only. Lets KAM open a past
                snapshot of this quote (read-only) via getOceanManualQuoteHistory. */}
            {readOnly && manualQuoteDetails?._id && (
              <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #EDEEF1" }}>
                <div
                  className="px-4 py-3.5"
                  style={{ borderBottom: "1px solid #F1F2F5" }}
                >
                  <div className="text-sm font-bold text-gray-800">Quotation history</div>
                </div>
                <div className="p-4 flex flex-col gap-2.5">
                  {historyLoading ? (
                    <div className="text-sm text-gray-500">Loading history...</div>
                  ) : historyEntries.length === 0 ? (
                    <div className="text-sm text-gray-500">No history available for this quotation.</div>
                  ) : (
                    historyEntries.map((entry, idx) => {
                      const isLatest = idx === 0;
                      const isViewed = viewedVersion === null ? isLatest : viewedVersion === entry.version;
                      return (
                        <div
                          key={entry.version}
                          className="rounded-xl px-4 py-3"
                          style={{
                            border: isViewed ? "1px solid #F5A623" : "1px solid #EEF0F3",
                            background: isViewed ? "#FFFAEF" : "#fff",
                          }}
                        >
                          <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-2.5 flex-wrap">
                              <Clock size={14} className="text-gray-400" />
                              <span className="text-sm font-bold text-gray-800">
                                {dayjs(entry.updatedAt).format("DD MMM YYYY")}
                              </span>
                              <span className="text-xs text-gray-400">
                                {dayjs(entry.updatedAt).format("HH:mm:ss")}
                              </span>
                              {isLatest && (
                                <span
                                  className="text-[10px] font-bold rounded-full px-2.5 py-1"
                                  style={{ background: "#F5A623", color: "#1f2937", letterSpacing: 0.4 }}
                                >
                                  LATEST
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{entry.updatedBy?.full_name || "—"}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (isLatest) {
                                  setViewedVersion(null);
                                  formik.setValues(buildFormValues(manualQuoteDetails));
                                } else {
                                  setViewedVersion(entry.version);
                                  formik.setValues(buildFormValues(entry.snapshot));
                                }
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              className="text-xs font-semibold px-3.5 py-2 rounded-lg"
                              style={{
                                border: "1px solid #EDEEF1",
                                background: isViewed ? "#F5A623" : "#fff",
                                color: isViewed ? "#1f2937" : "#4A463E",
                              }}
                            >
                              View state
                            </button>
                          </div>
                          {/* Reason for update — collected via the "Reason for
                              update" modal at Update-rates time; absent on the
                              initial CREATE version, which has nothing to justify. */}
                          {entry.updateRemarks && (
                            <div className="text-xs text-gray-600 mt-2 pl-[22px]">
                              <span className="font-semibold text-gray-500">Reason: </span>
                              {entry.updateRemarks}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {viewedVersion !== null && (
                  <div
                    className="flex items-center justify-between flex-wrap gap-3 px-4 py-3"
                    style={{ background: "#FFF8E7", borderTop: "1px solid #F0DEA3" }}
                  >
                    <span className="text-xs font-semibold" style={{ color: "#B86E00" }}>
                      Viewing read-only snapshot of this quotation.
                    </span>
                    <span
                      onClick={() => {
                        setViewedVersion(null);
                        formik.setValues(buildFormValues(manualQuoteDetails));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="text-xs font-bold cursor-pointer underline"
                      style={{ color: "#B86E00" }}
                    >
                      Back to latest
                    </span>
                  </div>
                )}
              </div>
            )}
          </Form>
          );
        }}
      </Formik>
    </MainContainer>
    </LocalizationProvider>
  );
};

export default OceanManualQuote;
